import React from 'react';
import { describeFamilyPrice } from '@shared/fees';
import { calculateAge } from '../../../utils/membership';
import type {
  FamilyChangeRequest,
  FamilyRequestMemberSummary,
} from './types';

const dollars = (cents: number) => `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;

const formatDate = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-AU', { year: 'numeric', month: 'short', day: 'numeric' });
};

const kindLabel = (kind: FamilyChangeRequest['kind']): string => {
  switch (kind) {
    case 'NEW_PERSON':
      return 'Add a new person';
    case 'EXISTING_MEMBER':
      return 'Add an existing member';
    case 'REMOVE_MEMBER':
      return 'Remove a member';
    case 'PORTAL_ACCESS':
      return 'Portal login request';
    default:
      return kind;
  }
};

const memberLabel = (
  id: string | null | undefined,
  members: Map<string, FamilyRequestMemberSummary>
): string => {
  if (!id) return '—';
  const m = members.get(id);
  if (!m) return id;
  return `${m.firstName} ${m.lastName}${m.memberNumber ? ` (${m.memberNumber})` : ''}`;
};

interface FamilyRequestDetailProps {
  request: FamilyChangeRequest;
  /** Member rows keyed by id, for the requester/existing/removal/new-primary names and numbers. */
  members: Map<string, FamilyRequestMemberSummary>;
}

/**
 * The read-only detail panel for a single FamilyChangeRequest, shown per
 * the contract: kind, requester member number, person details for
 * NEW_PERSON, existing member number + match status + consent for
 * EXISTING_MEMBER, removal + new primary for REMOVE_MEMBER, household
 * before -> after with `describeFamilyPrice`, and amount/payment status.
 */
const FamilyRequestDetail: React.FC<FamilyRequestDetailProps> = ({ request, members }) => {
  const requester = members.get(request.primaryMemberId);

  return (
    <div className="space-y-4" data-testid="family-request-detail">
      <div>
        <p className="text-sm font-medium text-gray-500">Request type</p>
        <p className="text-base font-semibold text-gray-900">{kindLabel(request.kind)}</p>
      </div>

      <div>
        <p className="text-sm font-medium text-gray-500">Requested by</p>
        <p className="text-sm text-gray-900">
          {requester
            ? `${requester.firstName} ${requester.lastName}`
            : request.primaryMemberId}
          {requester?.memberNumber && (
            <span className="ml-1 font-mono text-sm text-fishing-blue">
              ({requester.memberNumber})
            </span>
          )}
        </p>
      </div>

      {request.kind === 'NEW_PERSON' && request.person && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <p className="text-sm font-semibold text-gray-900 mb-2">Person details</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-700">
            <p>
              <span className="font-medium text-gray-500">Name: </span>
              {request.person.firstName} {request.person.lastName}
            </p>
            <p>
              <span className="font-medium text-gray-500">Date of birth: </span>
              {formatDate(request.person.dateOfBirth)}
            </p>
            {request.person.gender && (
              <p>
                <span className="font-medium text-gray-500">Gender: </span>
                {request.person.gender}
              </p>
            )}
            {request.person.shirtSize && (
              <p>
                <span className="font-medium text-gray-500">Shirt size: </span>
                {request.person.shirtSize}
              </p>
            )}
            {request.person.email && (
              <p>
                <span className="font-medium text-gray-500">Email: </span>
                {request.person.email}
              </p>
            )}
            {request.person.phone && (
              <p>
                <span className="font-medium text-gray-500">Phone: </span>
                {request.person.phone}
              </p>
            )}
            <p>
              <span className="font-medium text-gray-500">Wants a login: </span>
              {request.person.wantsPortalAccess ? 'Yes' : 'No'}
            </p>
          </div>
        </div>
      )}

      {request.kind === 'EXISTING_MEMBER' && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <p className="text-sm font-semibold text-gray-900 mb-2">Existing member</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-700">
            <p>
              <span className="font-medium text-gray-500">Member number entered: </span>
              {request.existingMemberNumber || '—'}
            </p>
            <p>
              <span className="font-medium text-gray-500">Date of birth entered: </span>
              {formatDate(request.existingMemberDobEntered)}
            </p>
            <p data-testid="existing-member-match">
              <span className="font-medium text-gray-500">Match status: </span>
              {request.existingMemberMatch || 'Not checked'}
            </p>
            <p data-testid="existing-member-consent">
              <span className="font-medium text-gray-500">Consent: </span>
              {request.existingMemberConsent || 'NOT_NEEDED'}
            </p>
          </div>
        </div>
      )}

      {request.kind === 'REMOVE_MEMBER' && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <p className="text-sm font-semibold text-gray-900 mb-2">Removal</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-700">
            <p>
              <span className="font-medium text-gray-500">Member to remove: </span>
              {memberLabel(request.removeMemberId, members)}
            </p>
            <p>
              <span className="font-medium text-gray-500">New primary: </span>
              {request.newPrimaryMemberId
                ? memberLabel(request.newPrimaryMemberId, members)
                : 'Not chosen (the committee will contact them)'}
            </p>
          </div>
        </div>
      )}

      {request.kind === 'PORTAL_ACCESS' && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <p className="text-sm font-semibold text-gray-900 mb-2">Portal login request</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-700">
            <p>
              <span className="font-medium text-gray-500">Member: </span>
              {memberLabel(request.existingMemberId, members)}
            </p>
            <p data-testid="portal-access-age">
              <span className="font-medium text-gray-500">Age: </span>
              {request.person?.dateOfBirth ? `${calculateAge(request.person.dateOfBirth)}` : '—'}
            </p>
            <p>
              <span className="font-medium text-gray-500">Email: </span>
              {request.person?.email || '—'}
            </p>
            <p>
              <span className="font-medium text-gray-500">Requested by: </span>
              {requester
                ? `${requester.firstName} ${requester.lastName}, the household primary`
                : 'The household primary'}
            </p>
          </div>
        </div>
      )}

      {(request.householdBefore || request.householdAfter) && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <p className="text-sm font-semibold text-gray-900 mb-2">Household</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-700">
            <p>
              <span className="font-medium text-gray-500">Before: </span>
              {request.householdBefore ? describeFamilyPrice(request.householdBefore) : '—'}
            </p>
            <p>
              <span className="font-medium text-gray-500">After: </span>
              {request.householdAfter ? describeFamilyPrice(request.householdAfter) : '—'}
            </p>
          </div>
        </div>
      )}

      <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">Amount due</p>
            <p className="text-lg font-bold text-fishing-blue">{dollars(request.amountDueCents)}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">Payment</p>
            <p className="text-sm font-semibold text-gray-900" data-testid="payment-status">
              {request.amountDueCents === 0
                ? 'Nothing owed'
                : request.paymentReceived
                  ? `Paid (${request.paymentMethod === 'ONLINE' ? 'online' : 'at the desk'})`
                  : `Not paid yet (${request.paymentMethod === 'ONLINE' ? 'online' : 'at the desk'})`}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FamilyRequestDetail;
